import usePrivateAxios from "@/common/api/usePrivateAxios"
import { defaultTransformerResponse } from "@/common/constants/defaultTransformerResponse.constant"
import { Mapper } from "@/common/services/mapper"
import { MetaResponse } from "@/common/types/metaResponse"
import { PermissionData } from "@/common/types/permissions"
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query"

type UsePermissionsProps<T> = {
  filters?: {
    search?: string
  }
  mapper?: (data: any) => T,
}

export const usePermissions = <T,>({
  filters: { search = '' } = {},
  mapper = Mapper.default<T>(),
}: UsePermissionsProps<T>) => {
  const privateAxios = usePrivateAxios()

  const {
    data: { data, meta } = defaultTransformerResponse<T>(),
    ...rest
  } = useInfiniteQuery({
    queryKey: ['permissions', search],
    queryFn: ({ pageParam }) => {
      return privateAxios.get('/permissions', {
        params: {
          search,
          page: pageParam
        },
      })
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage: { data: { data: any[], meta: MetaResponse }}) => {
      if (lastPage?.data?.meta?.current_page < lastPage?.data?.meta?.total_pages) {
        return lastPage.data.meta.current_page + 1
      }
    },
    select: (data) => {
      return {
        data: mapper(data?.pages?.reduce((acc, page) => {
          return [...acc, ...page?.data?.data]
        }, [] as PermissionData[])),
        meta: data?.pages[data?.pages?.length - 1].data.meta as MetaResponse
      }
    },
    placeholderData: keepPreviousData,
  })

  return { data, meta, ...rest }
}
